// =============================================================================
// BOUTON D'EXPORT PDF - RAPPORT DE RENTABILITÉ
// =============================================================================

import { useState } from 'react';
import { Download, Loader2, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { pdf } from '@react-pdf/renderer';
import { format } from 'date-fns';
import { RapportRentabilitePDF } from '@/components/pdf/RapportRentabilitePDF';
import type { ChantierRentabilite, KPIs } from '@/hooks/useChantiersFiltresRentabilite';

// =============================================================================
// PROPS
// =============================================================================

interface RentabiliteExportButtonProps {
  chantiers: ChantierRentabilite[];
  kpis: KPIs;
  filtreActif: string;
  disabled?: boolean;
}

// =============================================================================
// COMPOSANT
// =============================================================================

export function RentabiliteExportButton({
  chantiers,
  kpis,
  filtreActif,
  disabled = false,
}: RentabiliteExportButtonProps) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);

  const handleExport = async () => {
    if (chantiers.length === 0) {
      toast({
        title: 'Export impossible',
        description: 'Aucun chantier à exporter.',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);

    try {
      // Trier les chantiers par rentabilité décroissante
      const chantiersTries = [...chantiers].sort((a, b) => {
        const margePctA = a.metrics?.marge_finale_pct ?? -Infinity;
        const margePctB = b.metrics?.marge_finale_pct ?? -Infinity;
        return margePctB - margePctA;
      });

      // Générer le PDF
      const doc = (
        <RapportRentabilitePDF
          chantiers={chantiersTries}
          kpis={kpis}
          filtreActif={filtreActif}
        />
      );

      const blob = await pdf(doc).toBlob();
      const url = URL.createObjectURL(blob);

      // Nom du fichier
      const fileName = `rapport-rentabilite-${format(new Date(), 'yyyy-MM-dd')}.pdf`;

      // Télécharger
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast({
        title: 'PDF généré',
        description: `Le rapport de rentabilité a été téléchargé (${chantiers.length} chantier${chantiers.length > 1 ? 's' : ''}).`,
      });
    } catch (error) {
      console.error('[RentabiliteExport] Erreur:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de générer le PDF. Réessayez.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const isDisabled = disabled || loading || chantiers.length === 0;

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={handleExport}
      disabled={isDisabled}
      className="gap-2"
    >
      {loading ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : (
        <FileText className="w-4 h-4" />
      )}
      Exporter PDF
    </Button>
  );
}

export default RentabiliteExportButton;
